import { useState } from 'react';
import {
  GUIDANCE_COMPONENTS,
  STEP_ANCHORABLE_COMPONENTS,
  observedElementKey,
  updateGuidanceDto,
  type GuidanceContent,
  type GuidanceDto,
  type JourneyStepDto,
  type UpdateGuidance,
} from '@ederest/contracts';
import { Button } from '../ui/Button.js';
import { BilingualPreview } from './BilingualPreview.js';
import { TriggerEditor } from './TriggerEditor.js';
import { COMPONENT_LABEL, LANGUAGE_LABEL, isRtl } from './labels.js';

interface GuidanceEditorProps {
  guidance: GuidanceDto;
  languages: readonly string[];
  /** Steps of the journey: the guidance may be reattached to one of them. */
  steps: readonly JourneyStepDto[];
  busy?: boolean;
  onSave: (patch: UpdateGuidance) => void;
  onCancel: () => void;
}

type Draft = {
  contents: Record<string, GuidanceContent>;
  component: GuidanceDto['component'];
  trigger: GuidanceDto['trigger'];
  dismissalMemory: GuidanceDto['dismissalMemory'];
  reinforced: boolean;
  stepId: string;
  /** Key of the observed element the guidance anchors to (`observedElementKey`), not a selector. */
  anchorKey: string | null;
};

function emptyContent(): GuidanceContent {
  return { title: '', body: '', items: [] };
}

/**
 * The adjustment surface of functional §8.4 — the only place a guidance is
 * edited: text in every active language (checklist items included), component,
 * trigger and limits, dismissal memory, reinforced option of the guided tour,
 * step of attachment and anchor among the observed elements of the step. The
 * contract schema validates the patch before it leaves the browser (technical
 * §18.3); only what changed is sent.
 */
export function GuidanceEditor({ guidance, languages, steps, busy, onSave, onCancel }: GuidanceEditorProps) {
  const [draft, setDraft] = useState<Draft>(() => ({
    contents: Object.fromEntries(languages.map((code) => [code, guidance.contents[code] ?? emptyContent()])),
    component: guidance.component,
    trigger: guidance.trigger,
    dismissalMemory: guidance.dismissalMemory,
    reinforced: guidance.reinforced,
    stepId: guidance.stepId,
    anchorKey: guidance.anchor ? observedElementKey(guidance.anchor) : null,
  }));
  const [language, setLanguage] = useState(languages[0] ?? 'fr');
  const [error, setError] = useState<string | null>(null);

  const step = steps.find((candidate) => candidate.id === draft.stepId);
  const elements = step?.elements ?? [];
  const currentAnchorKey = guidance.anchor ? observedElementKey(guidance.anchor) : null;
  const needsAnchor = !STEP_ANCHORABLE_COMPONENTS.includes(draft.component);
  const content = draft.contents[language] ?? emptyContent();

  const setContent = (patch: Partial<GuidanceContent>) =>
    setDraft((d) => ({ ...d, contents: { ...d.contents, [language]: { ...content, ...patch } } }));

  const save = () => {
    const patch: UpdateGuidance = {};
    if (JSON.stringify(draft.contents) !== JSON.stringify(guidance.contents)) patch.contents = draft.contents;
    if (draft.component !== guidance.component) patch.component = draft.component;
    if (JSON.stringify(draft.trigger) !== JSON.stringify(guidance.trigger)) patch.trigger = draft.trigger;
    if (draft.dismissalMemory !== guidance.dismissalMemory) patch.dismissalMemory = draft.dismissalMemory;
    const reinforced = draft.component === 'guided_tour' && draft.reinforced;
    if (reinforced !== guidance.reinforced) patch.reinforced = reinforced;
    if (draft.stepId !== guidance.stepId) patch.stepId = draft.stepId;
    if (draft.anchorKey && draft.anchorKey !== currentAnchorKey) {
      // The anchor is one of the observed elements, taken whole: its selector when
      // the measurement had one, its label and kind otherwise. Never typed in.
      const element = elements.find((candidate) => observedElementKey(candidate) === draft.anchorKey);
      if (element) {
        patch.anchor = {
          selectors: [...element.selectors],
          elementType: element.elementType,
          label: element.label,
          role: element.role,
          rank: null,
        };
      }
    }
    const parsed = updateGuidanceDto.safeParse(patch);
    if (!parsed.success) {
      setError(parsed.error.issues.map((issue) => `${issue.path.join('.')} : ${issue.message}`).join(' · '));
      return;
    }
    if (Object.keys(parsed.data).length === 0) {
      onCancel();
      return;
    }
    setError(null);
    onSave(parsed.data);
  };

  return (
    <div className="editor" data-testid="guidance-editor">
      <div className="editor__grid">
        <label className="guidance__field">
          <span>Composant</span>
          <select
            value={draft.component}
            onChange={(e) => setDraft({ ...draft, component: e.target.value as GuidanceDto['component'] })}
          >
            {GUIDANCE_COMPONENTS.map((component) => (
              <option key={component} value={component}>
                {COMPONENT_LABEL[component]}
              </option>
            ))}
          </select>
        </label>

        <label className="guidance__field">
          <span>Étape de rattachement</span>
          <select
            value={draft.stepId}
            onChange={(e) => setDraft({ ...draft, stepId: e.target.value, anchorKey: null })}
          >
            {steps.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.position}. {candidate.name}
              </option>
            ))}
          </select>
        </label>

        {needsAnchor && (
          <label className="guidance__field">
            <span>Élément ciblé (observé sur l'étape)</span>
            <select
              value={draft.anchorKey ?? ''}
              onChange={(e) => setDraft({ ...draft, anchorKey: e.target.value || null })}
              disabled={elements.length === 0}
            >
              {draft.anchorKey && !elements.some((el) => observedElementKey(el) === draft.anchorKey) && (
                <option value={draft.anchorKey}>
                  {guidance.anchor?.label ?? guidance.anchor?.selectors[0] ?? draft.anchorKey} (actuel)
                </option>
              )}
              {elements.length === 0 && !draft.anchorKey && <option value="">Aucun élément observé</option>}
              {elements.map((element) => {
                const key = observedElementKey(element);
                return key ? (
                  <option key={key} value={key}>
                    {element.label ?? element.selectors[0]} · {element.elementType}
                  </option>
                ) : null;
              })}
            </select>
          </label>
        )}

        <label className="guidance__field">
          <span>Après fermeture par l'usager</span>
          <select
            value={draft.dismissalMemory}
            onChange={(e) =>
              setDraft({ ...draft, dismissalMemory: e.target.value as GuidanceDto['dismissalMemory'] })
            }
          >
            <option value="session">Ne plus afficher pendant la session</option>
            <option value="none">Peut réapparaître</option>
          </select>
        </label>

        {draft.component === 'guided_tour' && (
          <label className="editor__switch">
            <input
              type="checkbox"
              checked={draft.reinforced}
              onChange={(e) => setDraft({ ...draft, reinforced: e.target.checked })}
            />
            <span>Guidage renforcé : une seule décision par étape (spécification fonctionnelle §10.5.1)</span>
          </label>
        )}
      </div>

      <TriggerEditor
        value={draft.trigger}
        elements={elements}
        onChange={(trigger) => setDraft({ ...draft, trigger })}
      />

      <div className="guidance__langs" role="tablist" aria-label="Langue du contenu">
        {languages.map((code) => (
          <button
            key={code}
            type="button"
            role="tab"
            aria-selected={code === language}
            className={`guidance__lang${code === language ? ' guidance__lang--active' : ''}`}
            onClick={() => setLanguage(code)}
          >
            {LANGUAGE_LABEL[code] ?? code.toUpperCase()}
            {!draft.contents[code]?.body.trim() && (
              <span className="guidance__lang-dot" aria-label="contenu manquant" />
            )}
          </button>
        ))}
      </div>

      <div className="editor__content" dir={isRtl(language) ? 'rtl' : 'ltr'}>
        <label className="guidance__field">
          <span>Titre ({language.toUpperCase()})</span>
          <input
            value={content.title}
            onChange={(e) => setContent({ title: e.target.value })}
            maxLength={120}
            aria-label={`Titre ${language.toUpperCase()}`}
          />
        </label>
        <label className="guidance__field">
          <span>Message ({language.toUpperCase()})</span>
          <textarea
            value={content.body}
            onChange={(e) => setContent({ body: e.target.value })}
            rows={3}
            maxLength={600}
            aria-label={`Message ${language.toUpperCase()}`}
          />
        </label>
        <label className="guidance__field">
          <span>Éléments de liste ({language.toUpperCase()}), un par ligne</span>
          <textarea
            value={content.items.join('\n')}
            onChange={(e) =>
              setContent({
                items: e.target.value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .slice(0, 10),
              })
            }
            rows={3}
            aria-label={`Éléments ${language.toUpperCase()}`}
          />
        </label>
      </div>

      <details className="editor__preview">
        <summary>Prévisualiser dans les deux langues</summary>
        <BilingualPreview guidance={{ ...draft, contents: draft.contents }} languages={languages} />
      </details>

      {error && (
        <p className="guidance__warning" role="alert">
          {error}
        </p>
      )}

      <div className="guidance__editor-actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Annuler
        </Button>
        <Button variant="primary" onClick={save} disabled={busy}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
